import { CountryCode, getCountryCallingCode, isValidPhoneNumber, parsePhoneNumberWithError } from "libphonenumber-js";
// Metadata json (not re-exported by the public API): country_calling_codes lists each dial
// code's regions with the main region FIRST — the only source for "+1 belongs to the US"
// that doesn't hardcode geography in this repo.
import phoneMetadata from "libphonenumber-js/metadata.min.json";
import api from "@/api";
import type { Organization } from "@/types/organization/Organization";
import { FIELD_LENGTHS } from "@/helpers/Validation";
import { logError } from "@/helpers/logError";

/**
 * The country libphonenumber-js parses national-format numbers against when deciding validity.
 *
 * This MUST stay "US" to mirror the contact-api PhoneNumber model, which always parses persisted
 * numbers with `parsePhoneNumberWithError(value, { defaultCountry: "US" })` before `.isValid()`
 * (apps/contact-api/src/db/models/phoneNumber.ts). The accept/reject decision is therefore made
 * against the same country on both sides — see `isPhoneNumberValid` below.
 */
const SERVER_PHONE_VALIDATION_COUNTRY: CountryCode = "US";

/**
 * Synchronous phone-validity check used as the source of truth on submit (LSP-16732).
 *
 * vue-tel-input only reports validity via its async `@validate` event, which can race or never
 * emit on WebKit/Android — leaving a valid number permanently rejected and Save greyed out.
 * Recomputing validity directly from the string here means a missing emission can't block the
 * user, and surfaces invalid numbers as an inline field error on submit instead of an API 400.
 *
 * The validity decision is made against `SERVER_PHONE_VALIDATION_COUNTRY` (US) to match the
 * contact-api PhoneNumber model, which always validates with `defaultCountry: "US"`
 * (apps/contact-api/src/db/models/phoneNumber.ts — `parsePhoneNumberWithError(value, { defaultCountry: "US" }).isValid()`).
 * Using the org's `defaultCountryCode` here instead would accept a number valid only under the
 * org country (e.g. a national-format GB number) that the API then rejects with a generic error
 * toast — the exact pass-client/fail-API gap this gate exists to close. A `+`-prefixed E.164
 * number is parsed by its own country code regardless of the default, so international numbers
 * still validate. Reuses libphonenumber-js — no new dependency.
 */
export const isPhoneNumberValid = (phone: string | undefined | null): boolean => {
	const value = (phone ?? "").trim();
	if (!value) return false;
	try {
		return isValidPhoneNumber(value, SERVER_PHONE_VALIDATION_COUNTRY);
	} catch {
		// fall through to false
	}
	return false;
};

/**
 * LSP-17773: assemble a raw phone string into E.164 for the share dialog's live
 * preview and submit. Mirrors contact-api's normalizePhoneToE164WithCountry
 * (helpers/phoneNormalize.ts, defaultCountry "US") so what the dialog previews is
 * exactly what the share row stores. Uses isPossible() (right digit count) rather
 * than isValid() so a country-code mistake still previews an E.164 the user can
 * eyeball. Returns null when the input can't be assembled into E.164 yet.
 */
export const toE164 = (phone: string | undefined | null, defaultCountry: CountryCode = SERVER_PHONE_VALIDATION_COUNTRY): string | null => {
	const value = (phone ?? "").trim();
	if (!value) return null;
	try {
		const parsed = parsePhoneNumberWithError(value, { defaultCountry });
		return parsed.isPossible() ? parsed.format("E.164") : null;
	} catch {
		return null;
	}
};

const formatPhoneCache = new Map<string, string>();
const FORMAT_PHONE_CACHE_MAX = 1024;

const cachedFormat = (key: string, compute: () => string): string => {
	const hit = formatPhoneCache.get(key);
	if (hit !== undefined) return hit;
	const value = compute();
	if (formatPhoneCache.size >= FORMAT_PHONE_CACHE_MAX) {
		const oldest = formatPhoneCache.keys().next().value;
		if (oldest !== undefined) formatPhoneCache.delete(oldest);
	}
	formatPhoneCache.set(key, value);
	return value;
};

export const __resetFormatPhoneCacheForTests = (): void => {
	formatPhoneCache.clear();
};

export const __getFormatPhoneCacheSizeForTests = (): number => formatPhoneCache.size;

export const __hasFormatPhoneCacheKeyForTests = (defaultCountry: string | undefined, phone: string): boolean =>
	formatPhoneCache.has(`${defaultCountry ?? ""}|${phone}`);

export const formatPhone = (phone: string, defaultCountry?: CountryCode): string => {
	if (!phone) return phone ?? "";
	// Crash guard (LSP-16692): anything past the sane phone-input ceiling cannot be a
	// phone number. Return it untouched without parsing — and without caching it, so a
	// multi-megabyte value never becomes a multi-megabyte cache key — instead of letting
	// libphonenumber-js run repeatedly on a pathological string.
	if (phone.length > FIELD_LENGTHS.PHONE_INPUT) return phone;
	return cachedFormat(`${defaultCountry ?? ""}|${phone}`, () => {
		try {
			const parsed = parsePhoneNumberWithError(phone);
			if (parsed.isValid()) return parsed.formatInternational();
		} catch {
			// fall through
		}
		if (defaultCountry) {
			try {
				const parsed = parsePhoneNumberWithError(phone, { defaultCountry });
				if (parsed.isValid()) return parsed.formatInternational();
			} catch {
				// fall through
			}
		}
		if (!phone.startsWith("+")) {
			try {
				const parsed = parsePhoneNumberWithError("+" + phone);
				if (parsed.isValid()) return parsed.formatInternational();
			} catch {
				// fall through
			}
		}
		return phone;
	});
};

export const formatPhones = (phones: unknown, defaultCountry?: CountryCode): string => {
	if (!phones) return "";
	if (typeof phones !== "string") return "";
	return phones
		.split(",")
		.map((entry) => entry.trim())
		.filter((entry) => entry.length > 0)
		.map((entry) => formatPhone(entry, defaultCountry))
		.join(", ");
};

async function getOrgCountryFallback(): Promise<CountryCode> {
	try {
		const me: { organization?: Pick<Organization, "defaultCountryCode"> } = await api.getMe();
		if (me?.organization?.defaultCountryCode) {
			return me.organization.defaultCountryCode as CountryCode;
		}
	} catch {
		// ignore
	}
	return "US" as CountryCode;
}

export type VueTelInputOptions = Record<string, unknown>;
type VueTelPropsOverrides = Record<string, unknown> & { inputOptions?: VueTelInputOptions };
export type VueTelPropsMap = Record<string, unknown> & { inputOptions: VueTelInputOptions };

export const dedupeCountryCodes = (countries: readonly unknown[]): string[] => {
	const seen = new Set<string>();
	return countries.reduce<string[]>((uniqueCountries, country) => {
		if (typeof country !== "string") return uniqueCountries;
		const normalizedCountry = country.trim().toUpperCase();
		if (normalizedCountry && !seen.has(normalizedCountry)) {
			seen.add(normalizedCountry);
			uniqueCountries.push(normalizedCountry);
		}
		return uniqueCountries;
	}, []);
};

const getWindowVueTelProps = (): VueTelPropsOverrides => {
	return (window.VueTelProps || {}) as VueTelPropsOverrides;
};

export const getVueTelInputOptions = (overrides: VueTelInputOptions = {}): VueTelInputOptions => {
	const windowVueTelProps = getWindowVueTelProps();
	const globalInputOptions = (windowVueTelProps.inputOptions || {}) as VueTelInputOptions;

	return {
		...globalInputOptions,
		...overrides
	};
};

export const getVueTelProps = (overrides: VueTelPropsOverrides = {}): VueTelPropsMap => {
	const windowVueTelProps = getWindowVueTelProps();
	const mergedInputOptions = getVueTelInputOptions(overrides.inputOptions);
	const configuredPreferredCountries = overrides.preferredCountries ?? windowVueTelProps.preferredCountries;
	const preferredCountries = dedupeCountryCodes(Array.isArray(configuredPreferredCountries) ? configuredPreferredCountries : []);

	return {
		...windowVueTelProps,
		...overrides,
		preferredCountries,
		inputOptions: mergedInputOptions,
		autoDefaultCountry: true
	};
};

export const getCountryFromService = async (): Promise<CountryCode> => {
	try {
		return await fetch("https://ip2c.org/s")
			.then((response) => response.text())
			.then((response) => {
				const result = (response || "").toString();
				if (!result || result[0] !== "1") {
					throw new Error("ip2c failed");
				}

				return result.substring(2, 4) as CountryCode;
			});
	} catch {
		console.error("ip2c failed, falling back to organization country");
		return await getOrgCountryFallback();
	}
};

// LSP-17767 (Bugbot): "+<dial code>" for an ISO country, for defaulting a staged share
// recipient's country selector to the ORG's country instead of a hardcoded "+1" — a
// national-format number typed in a non-US org must stage, dedup, AND send under the same
// country. Falls back to "+1" for an unknown/missing ISO (matches toE164's US default).
export const dialCodeForCountry = (country: string | undefined | null): string => {
	try {
		return `+${getCountryCallingCode((country || "US") as CountryCode)}`;
	} catch {
		return "+1";
	}
};

/**
 * LSP-17773 (review): the ONE country-code list for every phone country <select>. The share
 * dialog and staged-row selects each shipped their own hardcoded 8-entry copy of the same
 * list, which silently excluded most of the world. Generate the full list from
 * libphonenumber-js metadata (already a dependency) and label each entry with the English
 * region name via Intl.DisplayNames (built into every supported browser; falls back to the
 * ISO code if unavailable).
 *
 * ONE OPTION PER DIAL CODE (Bugbot): the consuming selects bind the DIAL CODE as the option
 * value, so per-country options for shared codes (+1 covers 25 regions) made the browser
 * always display the first value match — "American Samoa" for a US org — and snap back when
 * another +1 country was chosen. Each dial code therefore appears once, labeled by its MAIN
 * region (from the metadata's country_calling_codes ordering) plus a count of the regions
 * sharing it — e.g. "+1 · United States & 24 more".
 */
let cachedPhoneCountryOptions: Array<[string, string]> | null = null;
export const phoneCountryOptions = (): Array<[string, string]> => {
	// Callers get a per-tuple COPY (review): returning the cache — or its tuples — by reference
	// would let one consumer's in-place mutation corrupt the list for every later caller.
	if (cachedPhoneCountryOptions) return cachedPhoneCountryOptions.map(([dialCode, label]) => [dialCode, label]);
	let regionNames: Intl.DisplayNames | null = null;
	try {
		regionNames = new Intl.DisplayNames(["en"], { type: "region" });
	} catch (error) {
		// Logged (review): universally supported for years, so a construction failure is worth
		// a Sentry breadcrumb even though the ISO-code fallback below keeps the list usable.
		logError("phoneCountryOptions: Intl.DisplayNames unavailable — falling back to ISO region codes", error);
		regionNames = null;
	}
	// The metadata's country_calling_codes maps every dial code to its regions with the MAIN
	// region first ("1" → ["US", ...], "44" → ["GB", ...]) — the ordering getCountries()
	// alphabetizes away. Guarded (review): the field is not part of the public API surface, so
	// a future dependency bump could drop or reshape it — fall back to an empty list rather
	// than crash the share dialog (phoneCountryOptionsPinned still surfaces the org default as
	// a bare entry). The whole build is inside the guard so a malformed ENTRY (not just a
	// missing map) also lands here instead of throwing inside a Vue computed.
	// Review (high): a handful of shared dial codes have HIGH-TRAFFIC secondary regions that the
	// "main region & N more" label makes unfindable by type-ahead (the old hardcoded list said
	// "US / Canada"). Named explicitly for those codes; everything else keeps main-region + count
	// (the metadata's second region is alphabetical — "Antigua & Barbuda" for +1 — so listing it
	// generically adds noise, and per-region options reintroduce the value-collision snap bug).
	const SHARED_CODE_LABELS: Record<string, string> = {
		"1": "United States / Canada & 23 more",
		"7": "Russia / Kazakhstan",
		"44": "United Kingdom & 3 more",
		"61": "Australia & 2 more"
	};
	try {
		// L10 (review): with moduleResolution "bundler" this import resolves through
		// libphonenumber-js's exports map to a typed wrapper whose MetadataJson type already
		// declares country_calling_codes — no cast needed; the runtime guard below still
		// covers a future bump reshaping the value.
		const countryCallingCodes = phoneMetadata.country_calling_codes as Record<string, string[]> | undefined;
		if (!countryCallingCodes || typeof countryCallingCodes !== "object") throw new Error("country_calling_codes missing");
		// Review medium #2: one malformed ENTRY must cost one option, not all 206 — the map body
		// gets its own guard, and only a broken MAP (missing/reshaped field) empties the list.
		const skipped: string[] = [];
		cachedPhoneCountryOptions = Object.entries(countryCallingCodes)
			.flatMap(([code, regions]) => {
				try {
					// Label = MAIN region + count. Native <select> type-ahead only matches label
					// PREFIXES, so no multi-name label makes a non-main region (e.g. Canada under +1)
					// type-ahead findable — and the metadata's second region is alphabetical
					// ("Antigua & Barbuda" for +1), which only adds noise. The dial code is the value
					// consumers assemble E.164 with, and it is identical for every region sharing it.
					if (!Array.isArray(regions) || regions.length === 0 || !/^\d+$/.test(code)) throw new Error("malformed entry");
					const mainLabel = regionNames?.of(regions[0]) ?? regions[0];
					const label =
						SHARED_CODE_LABELS[code] ?? (regions.length > 1 ? `${mainLabel} & ${regions.length - 1} more` : mainLabel);
					return [[`+${code}`, label] as [string, string]];
				} catch {
					skipped.push(code);
					return [];
				}
			})
			.sort((first, second) => first[1].localeCompare(second[1]));
		if (skipped.length > 0) {
			logError("phoneCountryOptions: skipped malformed metadata entries", null, { skipped });
		}
	} catch (error) {
		logError("phoneCountryOptions: libphonenumber metadata shape changed — country list unavailable", error);
		// Cached deliberately (review medium #9, semantics made explicit): the metadata is a
		// static bundled import, so a parse failure is deterministic for this build — retrying
		// per call would just re-log the same error. If this import ever becomes lazy/remote,
		// split "attempted" from "succeeded" so a transient failure can retry.
		cachedPhoneCountryOptions = [];
	}
	return cachedPhoneCountryOptions.map(([dialCode, label]) => [dialCode, label]);
};

/**
 * phoneCountryOptions with one dial code's entries pinned to the top — used to surface the
 * org default (share dialog) or a staged row's current code (staged row) first, so the
 * select never renders blank and the most likely choice is one tap away. Falls back to a
 * bare [dialCode, fallbackLabel] entry when the code is unknown to libphonenumber.
 */
export const phoneCountryOptionsPinned = (pinDialCode: string | undefined | null, fallbackLabel?: string): Array<[string, string]> => {
	const all = phoneCountryOptions();
	if (!pinDialCode) return all;
	const pinned = all.filter(([dialCode]) => dialCode === pinDialCode);
	const rest = all.filter(([dialCode]) => dialCode !== pinDialCode);
	// Fallback label never repeats the dial code (review: "+999 · +999") — when the caller has
	// no better name for an unknown code, say so.
	const bareLabel = fallbackLabel && fallbackLabel !== pinDialCode ? fallbackLabel : "Unknown region";
	return [...(pinned.length ? pinned : [[pinDialCode, bareLabel] as [string, string]]), ...rest];
};
